import { useState, useEffect, useRef } from "react";
import { useWorkspace } from "src/contexts/global/WorkspaceContext";

const STORAGE_PREFIX = "tf-ai-chat-pane-width";
const DEFAULT_WIDTH = 400;
export const MIN_WIDTH = 280;
export const MAX_WIDTH = 800;

const getStorageKey = (workspaceId) => `${STORAGE_PREFIX}-${workspaceId}`;

// Guard against corrupt/out-of-range localStorage values (NaN, negative,
// absurdly large) so the pane never renders at an invalid width.
const clampWidth = (value) => {
  if (!Number.isFinite(value)) {
    return DEFAULT_WIDTH;
  }
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, value));
};

/**
 * Persisted width for the consolidated AI chat pane (sidebar variant).
 * Width-only; pane open/close is governed by useAISidebar.
 */
const useResizableChatPaneWidth = () => {
  const workspace = useWorkspace();
  const wsId = workspace?.id || "default";

  const readStoredWidth = (id) => {
    try {
      const saved = localStorage.getItem(getStorageKey(id));
      return saved !== null ? clampWidth(parseInt(saved, 10)) : DEFAULT_WIDTH;
    } catch {
      return DEFAULT_WIDTH;
    }
  };

  const [width, setWidth] = useState(() => readStoredWidth(wsId));
  const prevWsIdRef = useRef(wsId);

  // Load the new workspace's saved width on switch, otherwise the prior
  // workspace's width would persist (and overwrite the new key).
  useEffect(() => {
    if (prevWsIdRef.current !== wsId) {
      prevWsIdRef.current = wsId;
      setWidth(readStoredWidth(wsId));
    }
  }, [wsId]);

  useEffect(() => {
    try {
      localStorage.setItem(getStorageKey(wsId), String(width));
    } catch {
      // localStorage unavailable
    }
  }, [width, wsId]);

  return { width, setWidth };
};

export default useResizableChatPaneWidth;
